import { makeValuesPredicate } from '@sgme/fp';
import type { Middleware } from 'redux';
import type { Action } from 'state/actions';
import * as dynamicActionTypes from 'state/dynamic/dynamicForm.actionTypes';
import { getQuoteResultFromQuoteId } from 'state/selectors';
import type { AppState } from 'state/state.model';
import type { IFrontLogger } from 'utils/loggers/FrontLogger';

const isCriticalAction = makeValuesPredicate([
  'QUOTE_STREAMING_TRADE_DONE' as const,
  'STREAMING_TRADE_AUTH_RECEIVED' as const,
  'QUOTE_ACKED' as const,
  'QUOTE_REQUESTED' as const,
  'QUOTE_CANCELLED' as const,
  'QUOTE_REMOVED' as const,
  'QUOTE_REQUEST_FAILED' as const,
  'QUOTE_RESULT_FAILED' as const,
  'QUOTE_RECEIVED' as const,

  // essential
  'QUOTE_PRICE_ENHANCED' as const,
  'QUOTE_TRADE_DONE' as const,
  'QUOTE_TRADE_FAILED' as const,
  'QUOTE_TRADE_REJECTED' as const,
  'QUOTE_TRADE_REQUESTED' as const,
  'QUOTE_TRADE_SENT' as const,
  'TRADE_AUTH_RECEIVED' as const,
  'TRADE_AUTH_TIMEOUT' as const,

  // Regulation
  'COST_AND_CHARGE_RECEIVED',
  'KID_DOCUMENT_RECEIVED',
  'KID_DOCUMENT_TIMEOUT',
  'PRODUCT_GOVERNANCE_ASSESSMENT_RECEIVED',
  'PRODUCT_GOVERNANCE_ASSESSMENT_TIMEOUT',
  'PRODUCT_GOVERNANCE_DOCUMENT_RECEIVED',
  'PRODUCT_GOVERNANCE_DOCUMENT_TIMEOUT',

  // New Dynamic screen
  dynamicActionTypes.DYNAMIC_BOOKING_REQUESTED,
  dynamicActionTypes.DYNAMIC_BOOKING_ACKED,
  dynamicActionTypes.DYNAMIC_BOOKING_REQUEST_FAILED,
] satisfies Action['type'][]);

interface FilteredAction {
  type: Action['type'];
  quoteId?: string;
  quoteExternalId?: string;
  tabId?: string;
}
export const logActionsMiddleware: (logger: IFrontLogger) => Middleware<{}, AppState> =
  logger =>
  ({ getState }) =>
  next =>
  async action => {
    if (isCriticalAction(action.type)) {
      const loggedAction = enrichAndFilterAction(getState(), action);
      logger.info('DISPATCHED ACTION {@Action}', loggedAction);
    }

    return await next(action);
  };

function enrichAndFilterAction(state: AppState, action: Action) {
  let loggedAction: FilteredAction = { type: action.type };
  if (isActionWithQuoteId(action)) {
    const quoteResult = getQuoteResultFromQuoteId(state, action.quoteId);
    loggedAction = { ...loggedAction, quoteId: action.quoteId, quoteExternalId: quoteResult?.quoteExternalId };
  }
  if (isActionWithTabId(action)) {
    loggedAction = { ...loggedAction, tabId: action.tabId };
  }
  return loggedAction;
}

type HasQuoteId<A> = A extends { quoteId: string } ? A : never;

function isActionWithQuoteId(action: Action): action is HasQuoteId<Action> {
  return 'quoteId' in action && typeof action.quoteId === 'string';
}
type HasTabId<A> = A extends { tabId: string } ? A : never;

function isActionWithTabId(action: Action): action is HasTabId<Action> {
  return 'tabId' in action && typeof action.tabId === 'string';
}
